/**
 * Compresión de la foto antes de encolarla.
 *
 * El backend también comprime, pero hacerlo aquí primero importa por tres
 * razones, y las tres se notan en el sitio del cliente: la foto ocupa menos en
 * la cola del móvil (Safari es tacaño con el almacenamiento), se sube más
 * rápido con mala cobertura, y gasta menos datos del LM.
 *
 * De 450 KB típicos a 150-250 KB, sin perder legibilidad del ticket.
 */

const LADO_MAXIMO = 1600;
const CALIDAD = 0.8;

export interface FotoPreparada {
  blob: Blob;
  nombre: string;
  bytesOriginales: number;
}

export async function prepararFoto(archivo: File): Promise<FotoPreparada> {
  const bytesOriginales = archivo.size;

  let bitmap: ImageBitmap;
  try {
    // `from-image` aplica la orientación EXIF. Sin esto los tickets hechos en
    // vertical se suben tumbados, que es como los ve luego quien aprueba.
    bitmap = await createImageBitmap(archivo, { imageOrientation: 'from-image' });
  } catch {
    // Si el navegador no puede decodificarla, mejor subir el original que
    // perder el ticket: el backend volverá a intentarlo con sharp.
    return { blob: archivo, nombre: archivo.name, bytesOriginales };
  }

  const escala = Math.min(1, LADO_MAXIMO / Math.max(bitmap.width, bitmap.height));
  const ancho = Math.round(bitmap.width * escala);
  const alto = Math.round(bitmap.height * escala);

  const lienzo = document.createElement('canvas');
  lienzo.width = ancho;
  lienzo.height = alto;

  const contexto = lienzo.getContext('2d');
  if (!contexto) {
    bitmap.close();
    return { blob: archivo, nombre: archivo.name, bytesOriginales };
  }

  contexto.drawImage(bitmap, 0, 0, ancho, alto);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolver) => {
    lienzo.toBlob(resolver, 'image/jpeg', CALIDAD);
  });

  if (!blob) {
    return { blob: archivo, nombre: archivo.name, bytesOriginales };
  }

  return {
    blob,
    nombre: nombreDeTicket(),
    bytesOriginales,
  };
}

/** Nombre legible y ordenable, que es lo que verá quien apruebe la nota. */
function nombreDeTicket(): string {
  const ahora = new Date();
  const dosDigitos = (n: number) => String(n).padStart(2, '0');
  const fecha = `${ahora.getFullYear()}${dosDigitos(ahora.getMonth() + 1)}${dosDigitos(
    ahora.getDate(),
  )}`;
  const hora = `${dosDigitos(ahora.getHours())}${dosDigitos(ahora.getMinutes())}${dosDigitos(
    ahora.getSeconds(),
  )}`;
  return `ticket-${fecha}-${hora}.jpg`;
}
